import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { EVENTS, useTrackingWithSort, VARIANTS } from '@/api/tracking'
import { Text, TextLink, useToast } from '@machobear/design-system'
import { useQuery } from '@tanstack/react-query'
import { isUndefined } from 'lodash'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import posthog from 'posthog-js'

import config from '@/config'
import {
  createFlag,
  getCannedTemplateInquiries,
  getFlag,
  postCannedTemplate,
  putCannedTemplate,
  startPublicChat,
  tenantStartChat,
  updateFlag
} from '@/ports'
import { FetchResponse } from '@/ports/api'
import useFlag from '@/hooks/use-flag'
import useGoogleEventUserData from '@/hooks/use-google-event-user-data'
import useInquiryCannedTemplates from '@/hooks/use-inquiry-canned-templates'
import usePublicChatSession from '@/hooks/use-public-chat-session'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import { useMessageModalContext } from '@/features/ListingsMap/map-list/context'
import { useAlerts } from '@/components/alert/useAlerts'
import { queryClient } from '@/components/query-client'

import useAddRecentMessage from '../hooks/recent-messages/use-add-recent-message'
import {
  contactSimilarListingFunc,
  sendInquiryMessage
} from './inquiryMessageUtil'

export type InquiryMessage = { id?: string; content: string }

export const useInquiryMessage = (isLoggedIn: boolean) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { sendAlert, sendAlertIfLoggedIn } = useAlerts()
  const showToast = useToast()
  const { isAutoSendingMessage } = useMessageModalContext()
  const isAutoSendingMessageRef = useRef(isAutoSendingMessage)

  useEffect(() => {
    isAutoSendingMessageRef.current = isAutoSendingMessage
  }, [isAutoSendingMessage])

  const fallback: InquiryMessage = useMemo(
    () => ({
      id: undefined,
      content: t(
        'b.listing.view.message_default.text',
        'Hi there, I saw your rental listing on liv.rent and would like to learn more. Please let me know what the next step would be. Thank you!'
      )
    }),
    [t]
  )
  const { data: defaultMoveInDateFlag } = useFlag({
    flagName: 'default_move_in_date'
  })
  const { data: contactSimilarListingFlag } = useFlag({
    flagName: 'contact_similar_listings'
  })
  const { data: inquiryCannedTemplate } = useInquiryCannedTemplates()
  const { data: publicSession } = usePublicChatSession()
  const { addRecentMessage, addRecentMessages } = useAddRecentMessage()
  const userData = useGoogleEventUserData()
  const { mutateAsync: track } = useTrackingWithSort()
  const isCityPage = router.asPath.includes('city')
  // matches /rental-listings/detail/... or /rental-listing/12345
  const isDetailPage = /^\/rental-listings\/(detail\/.*|\d+)$/.test(
    router.asPath
  )

  const getLocalStorageInquiry = (): FetchResponse<InquiryMessage[]> => {
    const inquiryFormValuesStr = localStorage.getItem('inquiry_form_values')

    let storedMessage

    if (inquiryFormValuesStr) {
      try {
        const inquiryFormValues = JSON.parse(inquiryFormValuesStr)
        if (inquiryFormValues?.message) {
          storedMessage = inquiryFormValues.message
        }
      } catch (error) {
        // don't care
      }
    }

    return {
      response: { ok: true } as Response,
      body: storedMessage
        ? [{ id: undefined, content: storedMessage }]
        : [fallback]
    }
  }

  const queryFn = async (): Promise<FetchResponse<InquiryMessage[]>> => {
    if (isLoggedIn) {
      return getCannedTemplateInquiries() as Promise<
        FetchResponse<InquiryMessage[]>
      >
    } else {
      return getLocalStorageInquiry()
    }
  }

  const { data: messageData, isLoading } = useQuery(
    ['useInquiryMessage', isLoggedIn],
    queryFn,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      select: (data: FetchResponse<InquiryMessage[]>): InquiryMessage => {
        if (data.response.ok && data.body && data.body.length > 0) {
          return pick(data.body[0], ['id', 'content'])
        }
        return fallback
      }
    }
  )

  const saveUserFlag = async (keyName: string, keyValue: string | boolean) => {
    const { response, body } = await getFlag({ flagName: keyName }, undefined)

    let response2

    if (response.ok) {
      response2 = await updateFlag(
        {
          body: {
            key_name: keyName,
            key_value:
              typeof keyValue === 'string' ? keyValue : JSON.stringify(keyValue)
          }
        },
        { id: body[keyName]?.id }
      )
    } else if (response.status === 409) {
      // flag does not exist
      response2 = await createFlag({
        body: {
          key_name: keyName,
          key_value:
            typeof keyValue === 'string' ? keyValue : JSON.stringify(keyValue)
        }
      })
    } else {
      return { response, body }
    }

    return response2
  }

  const isFormValuesValid = useCallback(
    (formValues: any) => {
      if (isLoggedIn) {
        return (
          !!formValues.move_in_date &&
          !!formValues.message &&
          !isUndefined(formValues.contact_similar_listings)
        )
      } else {
        return (
          !!formValues.first_name &&
          !!formValues.last_name &&
          !!formValues.move_in_date &&
          !!formValues.message &&
          !isUndefined(formValues.contact_similar_listings)
        )
      }
    },
    [isLoggedIn]
  )

  const abtestTracking = useCallback(
    (listingId: string) => {
      return track({
        event_name: isLoggedIn
          ? EVENTS.INQUIRY_SENT
          : EVENTS.PUBLIC_CHAT_STARTED,
        metadata: {
          listingId,
          source: isCityPage ? 'listing-card-modal' : 'listing-detail'
        },
        variant_group: VARIANTS.LISTING_SORT,
        referer: document.referrer
      })
    },
    [track, isLoggedIn, isCityPage]
  )

  // public
  const showInquiryMessageToast = useCallback(
    (params?: {
      listing: any
      contact_similar_listings: boolean
      formValues: any
    }) => {
      if (params) {
        const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault()

          showLoginModal({
            mode: 'register',
            eventId: '',
            data: {
              username: params.formValues.email,
              first_name: params.formValues.first_name,
              last_name: params.formValues.last_name,
              account_type: 'tenant'
            },
            redirectUrl: `/chat`,
            asyncAfterLogin: async () => {
              if (params.contact_similar_listings) {
                await sendAlert(params.listing)
              }
            },
            t
          })
        }
        showToast({
          tone: 'positive' as const,
          message: t('b.listing.view.message_sent.title', 'Message Sent!'),
          description: (
            <Text>
              <TextLink href={''} onClick={onClick}>
                {t('View chat messages')}
              </TextLink>
            </Text>
          )
        })
      } else {
        showToast({
          tone: 'positive' as const,
          message: t('b.listing.view.message_sent.title', 'Message Sent!'),
          description: (
            <Text>
              <TextLink href={`${config.DASHBOARD_URL}/chat`}>
                {t('View chat messages')}
              </TextLink>
            </Text>
          )
        })
      }
    },
    [sendAlert, showToast, t]
  )

  const getFormValues = useCallback(async () => {
    if (isLoggedIn) {
      return {
        move_in_date: defaultMoveInDateFlag ?? 'asap',
        contact_similar_listings:
          contactSimilarListingFlag === 'false' ? false : true,
        message: (inquiryCannedTemplate as any)?.content ?? fallback.content
      }
    } else {
      const {
        first_name = publicSession?.first_name || '',
        last_name = publicSession?.last_name || '',
        email = publicSession?.email || '',
        message = fallback.content,
        move_in_date = 'asap',
        contact_similar_listings = true
      } = JSON.parse(localStorage.getItem('inquiry_form_values') ?? '{}')

      return {
        first_name,
        last_name,
        email,
        message,
        move_in_date,
        contact_similar_listings
      }
    }
  }, [
    isLoggedIn,
    defaultMoveInDateFlag,
    contactSimilarListingFlag,
    inquiryCannedTemplate,
    fallback,
    publicSession
  ])

  const saveFormValues = useCallback(
    async (formValues: any) => {
      if (isLoggedIn) {
        if (inquiryCannedTemplate?.id && inquiryCannedTemplate?.content) {
          if (formValues.message != inquiryCannedTemplate.content) {
            await putCannedTemplate(
              {
                body: {
                  content: formValues.message?.trim()
                    ? formValues.message?.trim()
                    : fallback.content
                }
              },
              { templateId: inquiryCannedTemplate.id }
            )
          }
        } else {
          await postCannedTemplate({
            body: {
              content: formValues.message?.trim()
                ? formValues.message?.trim()
                : fallback.content,
              template_type: 'inquiries'
            }
          })
        }

        await Promise.all([
          saveUserFlag('default_move_in_date', formValues.move_in_date),
          saveUserFlag(
            'contact_similar_listings',
            formValues.contact_similar_listings
          )
        ])
        queryClient.removeQueries(['useInquiryCannedTemplates'])
        queryClient.removeQueries(['useFlag', 'default_move_in_date'])
        queryClient.removeQueries(['useFlag', 'contact_similar_listings'])
      } else {
        localStorage.setItem(
          'inquiry_form_values',
          JSON.stringify(omit(formValues, ['captcha']))
        )
        queryClient.removeQueries(['usePublicChatSession'])
      }
    },
    [
      fallback.content,
      inquiryCannedTemplate?.content,
      inquiryCannedTemplate?.id,
      isLoggedIn
    ]
  )

  const sendInquiryMessageAfterLogin = useCallback(
    async (
      onSuccess: () => void,
      params: {
        listingId: string
        formValues: any
        contact_similar_listings: boolean
        source: 'listing-card-actions' | 'listing-detail'
      }
    ) => {
      if (!params) {
        return { ok: false, message: 'missing params' }
      }

      await saveFormValues({
        ...params.formValues,
        contact_similar_listings: params.contact_similar_listings
      })

      const result1 = await sendInquiryMessage({
        isLoggedIn: true,
        listingId: params.listingId,
        formValues: pick(params.formValues, ['message', 'move_in_date'])
      })

      if (!result1.ok) {
        showToast({
          tone: 'critical',
          message: result1.message ?? 'Unable to contact the listing(s)'
        })
      }

      if (!params.contact_similar_listings) {
        onSuccess()
        return
      }

      const contactResult = await contactSimilarListingFunc({
        isLoggedIn: true,
        listingId: params.listingId,
        formValues: params.formValues
      })
      if (!contactResult.ok) {
        showToast({
          tone: 'critical',
          message: contactResult.message ?? 'Unable to contact the listing(s)'
        })
        return
      }

      onSuccess()
    },
    [showToast, saveFormValues]
  )

  const sendMessageModalTrackingOnSubmit = useCallback(
    async (
      listingId: string,
      formValues: any,
      contact_similar_listings: boolean
    ) => {
      const eventData = isLoggedIn
        ? {
            event: `logged-${
              isCityPage ? 'city-page-' : ''
            }inquire-submit-${listingId}`,
            ...userData,
            similarListing: contact_similar_listings
              ? 'similarlisting'
              : 'nosimilarlisting'
          }
        : {
            event: `listing-${
              isCityPage ? 'city-page-' : ''
            }inquire-submit-${listingId}`,
            userEmail: formValues.email,
            userFirstName: formValues.first_name,
            userLastName: formValues.last_name,
            similarListing: contact_similar_listings
              ? 'similarlisting'
              : 'nosimilarlisting'
          }

      window.dataLayer.push(eventData)
    },
    [isLoggedIn, isCityPage, userData]
  )

  const sendMessageModalTrackingOnSuccess = useCallback(
    async (
      listingId: string,
      formValues: any,
      contact_similar_listings: boolean
    ) => {
      abtestTracking(listingId)
      const eventData = isLoggedIn
        ? {
            event: `logged-${
              isCityPage ? 'city-page-' : ''
            }inquire-submit-sent`,
            ...userData,
            similarListing: contact_similar_listings
              ? 'similarlisting'
              : 'nosimilarlisting'
          }
        : {
            event: `listing-${
              isCityPage ? 'city-page-' : ''
            }inquire-submit-sent`,
            userEmail: formValues.email,
            userFirstName: formValues.first_name,
            userLastName: formValues.last_name,
            similarListing: contact_similar_listings
              ? 'similarlisting'
              : 'nosimilarlisting'
          }

      window.dataLayer.push(eventData)

      const secondaryEvent = `listing-${
        isCityPage ? 'city-page-' : ''
      }inquire-submit-sent-${
        contact_similar_listings ? '' : 'no-'
      }similar-listings`

      window.dataLayer.push({
        event: secondaryEvent
      })
    },
    [abtestTracking, isLoggedIn, isCityPage, userData]
  )

  const autosendInquiryMessage = useCallback(
    async ({
      listing,
      XTempAccessKey,
      onTokenExpired,
      onFormValuesInvalid,
      onComplete
    }: {
      listing: any
      XTempAccessKey: string | null
      onTokenExpired: () => void
      onFormValuesInvalid: () => void
      onComplete: () => void
    }) => {
      try {
        let formValues: any = await getFormValues()

        if (!isFormValuesValid(formValues)) {
          showToast({
            tone: 'critical',
            message: 'Please fill in all fields'
          })
          onFormValuesInvalid()
          return
        }

        const contact_similar_listings = formValues.contact_similar_listings
        formValues = omit(formValues, 'contact_similar_listings')

        sendMessageModalTrackingOnSubmit(
          listing.listing_id,
          formValues,
          contact_similar_listings
        )

        const { response, body }: any = isLoggedIn
          ? await tenantStartChat(formValues, { listingId: listing.listing_id })
          : await startPublicChat(
              { body: omit(formValues, 'captcha') },
              { listingId: listing.listing_id },
              undefined,
              {
                headers: {
                  'X-Temp-Access-Key': XTempAccessKey
                }
              }
            )

        if (!response.ok) {
          if (body?.message_key === 'err.public_chat_login_required') {
            showToast({
              tone: 'critical',
              message: body?.message ?? 'Please login first'
            })
            onComplete()
          } else if (body?.message_key === 'err.captcha_token_invalid') {
            onTokenExpired()
          } else if (
            body?.message_key === 'err.action_not_allowed_for_resource'
          ) {
            onTokenExpired()
          } else {
            showToast({
              tone: 'critical',
              message: body?.message ?? 'Unable to contact the listing(s)'
            })
            onComplete()
          }
          return
        }

        if (!isLoggedIn) {
          XTempAccessKey = response.headers.get('X-Temp-Access-Key')
          if (XTempAccessKey) {
            sessionStorage.setItem('X-Temp-Access-Key', XTempAccessKey)
          }
        }

        addRecentMessage(listing.listing_id)

        // POSTHOG EXPERIMENT - T19532
        if (!isLoggedIn) {
          posthog.capture('listing_inquiry_autosend_success')
        }

        sendMessageModalTrackingOnSuccess(
          listing.listing_id,
          formValues,
          contact_similar_listings
        )

        if (contact_similar_listings) {
          await sendAlertIfLoggedIn(listing)
          const contactResult = await contactSimilarListingFunc({
            isLoggedIn,
            listingId: listing.listing_id,
            formValues,
            XTempAccessKey
          })
          if (!contactResult.ok) {
            showToast({
              tone: 'critical',
              message:
                contactResult.message ??
                'Unable to contact the similar listings'
            })
            onComplete()
            return
          }

          addRecentMessages(contactResult?.successful_listings)
        }

        onComplete()

        if (isLoggedIn) {
          showInquiryMessageToast()
        } else {
          showInquiryMessageToast({
            listing,
            formValues,
            contact_similar_listings
          })
        }

        return
      } catch (error) {
        showToast({
          tone: 'critical',
          message: 'Unable to contact the listing(s)'
        })
        onComplete()
      }
    },
    [
      getFormValues,
      isFormValuesValid,
      sendMessageModalTrackingOnSubmit,
      isLoggedIn,
      addRecentMessage,
      sendMessageModalTrackingOnSuccess,
      showToast,
      sendAlertIfLoggedIn,
      addRecentMessages,
      showInquiryMessageToast
    ]
  )

  return {
    fallback,
    isLoading,
    message: messageData,
    defaultMessage: fallback,
    isCityPage,
    isDetailPage,
    showInquiryMessageToast,
    sendInquiryMessageAfterLogin,
    getFormValues,
    saveFormValues,
    autosendInquiryMessage,
    sendMessageModalTrackingOnSubmit,
    sendMessageModalTrackingOnSuccess
  }
}
